/**
 * React hook for browser notifications
 *
 * Provides easy access to browser notification functionality
 * and handles permission requests.
 */

import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import {
  initBrowserNotifications,
  cleanupBrowserNotifications,
  requestNotificationPermission,
  getNotificationPermission,
  isNotificationSupported,
  showNotificationWithSound,
  playNotificationSound,
  NotificationPermission,
} from '@/services/browser-notifications';

interface UseBrowserNotificationsReturn {
  /**
   * Whether browser notifications are supported
   */
  isSupported: boolean;

  /**
   * Current permission status
   */
  permission: NotificationPermission;

  /**
   * Whether permission has been granted
   */
  isGranted: boolean;

  /**
   * Whether permission was denied (can't ask again)
   */
  isDenied: boolean;

  /**
   * Whether we need to ask for permission
   */
  needsPermission: boolean;

  /**
   * Request notification permission
   */
  requestPermission: () => Promise<NotificationPermission>;

  /**
   * Show a notification with sound
   */
  showNotification: (title: string, body?: string, onClick?: () => void) => void;

  /**
   * Just play the notification sound
   */
  playSound: () => void;

  /**
   * Whether the hook is ready (user is authenticated and service initialized)
   */
  isReady: boolean;
}

export function useBrowserNotifications(): UseBrowserNotificationsReturn {
  const { user } = useAuth();
  const [permission, setPermission] = useState<NotificationPermission>(() => getNotificationPermission());
  const [isReady, setIsReady] = useState(false);

  // Initialize when user is authenticated
  useEffect(() => {
    if (!user?.id) {
      setIsReady(false);
      return;
    }

    // Initialize the service
    initBrowserNotifications(user.id).then(() => {
      setIsReady(true);
    });

    // Update permission state
    setPermission(getNotificationPermission());

    return () => {
      cleanupBrowserNotifications();
      setIsReady(false);
    };
  }, [user?.id]);

  // Request permission handler
  const requestPermission = useCallback(async () => {
    const result = await requestNotificationPermission();
    setPermission(result);
    return result;
  }, []);

  // Show notification handler
  const showNotification = useCallback((title: string, body?: string, onClick?: () => void) => {
    showNotificationWithSound(title, { body, onClick });
  }, []);

  // Play sound handler
  const playSound = useCallback(() => {
    playNotificationSound();
  }, []);

  return {
    isSupported: isNotificationSupported(),
    permission,
    isGranted: permission === 'granted',
    isDenied: permission === 'denied',
    needsPermission: permission === 'default',
    requestPermission,
    showNotification,
    playSound,
    isReady,
  };
}
